export { formatReportData, validateReportData } from '../lib/reportGenerator';

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * The single HTML report template. Both the print button on ReportView and the
 * preview/PDF path on ResultsView render through this function — the two used
 * to carry separate copies of the markup and had already drifted apart in the
 * methodology wording. There is one copy now; ReportView re-exports it as
 * `generatePrintHTML`.
 *
 * Order is deliberate: an AHJ sees what property this is, then the
 * determination, then the arithmetic behind it, before any supporting detail.
 */
export function generateHTMLReport(data: any): string {
  const isPassing = data.adequacy_result === 'PASS';
  const safe = (value: any) => escapeHtml(String(value || ''));
  // Attribute values additionally need their quotes neutralised.
  const attr = (value: unknown) => safe(value).replace(/"/g, '&quot;');
  const hasMCAEquipment = (data.equipment_list || []).some((eq: any) => eq.source === 'MCA');

  // Report basis. The traditional (NEC 220.83 / 120.83) basis swaps the title,
  // summary, worksheet and methodology sections; identity, preparer,
  // determination, disclaimer and signature are shared. Section numbers come
  // from the edition pack via trad_citation — never hardcoded, because the
  // 2026 NEC renumbered Article 220 into Article 120.
  const isTraditional = data.method === 'traditional';
  // The measured method's own number is edition-dependent for exactly the same
  // reason: 220.87 under 2020/2023, 120.87 under the 2026 Article 120 reorg.
  // formatReportData resolves it from the project's edition; nothing below
  // writes a section number of its own.
  const cite = safe(data.measured_citation || '220.87');
  const conductorRefs = data.measured_conductor_refs
    ? ` Conductor and overcurrent device sizing for individual circuits remains subject to NEC ${safe(data.measured_conductor_refs)} as applicable.`
    : ' Conductor and overcurrent device sizing for individual circuits remains subject to the applicable branch-circuit, feeder, and service provisions of the Code.';
  const reportTitle = isTraditional
    ? `NEC ${safe(data.trad_citation)} Service Adequacy Evaluation Report`
    : `NEC ${cite} Service Adequacy Evaluation Report`;

  const tradSummaryRows = isTraditional
    ? (data.trad_summary_rows || [])
        .map((row: any) => {
          const cls =
            row.kind === 'highlight'
              ? ' class="highlight-row"'
              : row.kind === 'total'
                ? ' class="total-row"'
                : '';
          return `<tr${cls}><td>${safe(row.label)}</td><td>${safe(row.value)}</td></tr>`;
        })
        .join('')
    : '';

  const tradWorksheetRows = isTraditional
    ? (data.trad_worksheet_rows || [])
        .map(
          (row: any) => `
      <tr>
        <td>${safe(row.label)}</td>
        <td>${safe(row.origin)}</td>
        <td style="text-align:right">${safe(row.connected_va)}</td>
        <td>${safe(row.treatment)}</td>
        <td style="text-align:right">${safe(row.demand_va)}</td>
      </tr>`
        )
        .join('')
    : '';

  const propertyLine = [data.customer_address, data.customer_city_state_zip]
    .filter(Boolean)
    .join(', ');

  const identityMeta = [
    data.customer_name,
    data.permit_number ? `Permit ${data.permit_number}` : '',
    data.ahj_jurisdiction,
    data.report_date,
  ]
    .filter(Boolean)
    .map((part: string) => safe(part))
    .join(' &nbsp;&middot;&nbsp; ');

  // 2026 measured position: two more columns, the Table 120.83 percentage and
  // what the row adds to the adjusted demand (newLoadFactorDisclosure.ts).
  const factored = !isTraditional && data.new_load_factors_applied === true;
  const equipmentRows = (data.equipment_list || []).map((eq: any) => {
    const factorCells = factored ? `
      <td>${safe(eq.demand_factor)}</td>
      <td>${safe(eq.counted_kva) || '0.00'}</td>` : '';
    const reasonRow = !isTraditional && eq.concurrency_reason
      ? `<tr><td colspan="${factored ? 11 : 9}" style="font-size: 10pt; color: #6b7280; font-style: italic; padding: 3px 8px; border-top: none; background: #f9fafb;">Concurrency basis: ${safe(eq.concurrency_reason)}</td></tr>`
      : '';
    // Concurrency is a measured-method (220.87) concept; the traditional
    // schedule omits the column rather than print a meaningless No.
    const concurrentCell = isTraditional ? '' : `
      <td>${safe(eq.is_concurrent) || 'No'}</td>`;
    return `
    <tr>
      <td>${safe(eq.equipment_name) || 'N/A'}</td>
      <td>${safe(eq.equipment_type) || 'N/A'}</td>
      <td>${safe(eq.source) || 'Nameplate'}</td>
      <td>${safe(eq.quantity) || '1'}</td>
      <td>${safe(eq.voltage) || 'N/A'}V</td>
      <td>${safe(eq.amps) || 'N/A'}</td>
      <td>${safe(eq.kw) || 'N/A'}</td>
      <td>${safe(eq.kva) || '0.00'}</td>${concurrentCell}${factorCells}
    </tr>${reasonRow}`;
  }).join('');

  // Equipment taken out with this work (removedLoadDisclosure.ts). Measured
  // reports show whether each item ran in the peak's season and, under 2026,
  // its credit; the traditional report lists them as not counted.
  const removedRows = (data.removed_list || []).map((eq: any) => `
    <tr>
      <td>${safe(eq.equipment_name) || 'N/A'}</td>
      <td>${safe(eq.equipment_type) || 'N/A'}</td>
      <td>${safe(eq.source) || 'Nameplate'}</td>
      <td>${safe(eq.quantity) || '1'}</td>
      <td>${safe(eq.voltage) || 'N/A'}V</td>
      <td>${safe(eq.amps) || 'N/A'}</td>
      <td>${safe(eq.kw) || 'N/A'}</td>
      <td>${safe(eq.kva) || '0.00'}</td>${isTraditional ? '' : `
      <td>${safe(eq.is_concurrent) || 'No'}</td>`}${data.removed_credit_applied ? `
      <td>${safe(eq.credit_kva)}</td>` : ''}
    </tr>${!isTraditional && eq.concurrency_reason ? `<tr><td colspan="${data.removed_credit_applied ? 10 : 9}" style="font-size: 10pt; color: #6b7280; font-style: italic; padding: 3px 8px; border-top: none; background: #f9fafb;">Season basis: ${safe(eq.concurrency_reason)}</td></tr>` : ''}`).join('');
  const removedSection = (data.removed_list || []).length > 0 ? `
  <h2>Equipment Removed With This Work</h2>
  <table>
    <thead>
      <tr>
        <th>Equipment Name</th>
        <th>Type</th>
        <th>Source</th>
        <th>Qty</th>
        <th>Voltage</th>
        <th>Amps</th>
        <th>kW</th>
        <th>kVA</th>${isTraditional ? '' : `
        <th>Ran at Peak Season</th>`}${data.removed_credit_applied ? `
        <th>Credit kVA</th>` : ''}
      </tr>
    </thead>
    <tbody>
      ${removedRows}
    </tbody>
  </table>
  ${data.removed_disclosure ? `<p>${safe(data.removed_disclosure)}</p>` : ''}
  ` : '';

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${reportTitle}</title>
  <style>
    @media print {
      @page { margin: 0.75in; }
      body { margin: 0; }
      .page-break { page-break-before: always; }
      /* The watermark must survive printing to PDF — browsers drop background
         colours by default, so this is drawn as text, not a background image. */
      .draft-watermark { display: block !important; }
      /* The determination and the calculation it rests on must not be split
         across a page boundary. */
      .result-box, .summary-table, .figure { page-break-inside: avoid; }
    }

    /* The daily-peak figure. Drawn as inline SVG so it prints as part of the
       document; the caption carries the facts for a reader without the chart. */
    .figure { margin: 16px 0 8px; }
    .figure svg { display: block; width: 100%; height: auto; }
    .figure-caption { font-size: 10pt; line-height: 1.45; margin: 6px 0 0; }

    /* Firm letterhead. Present only for an Enterprise firm that uploaded a
       logo; bounded so it reads as stationery, not a poster. The image is
       embedded in the document, so print and the server PDF fetch nothing. */
    .letterhead {
      text-align: center;
      margin: 0 0 14px;
    }

    .letterhead img {
      max-height: 0.6in;
      max-width: 2.6in;
      width: auto;
      height: auto;
    }

    /* Draft marking. Present only when the project still has unresolved
       completion blockers, so a report can never look finished while the
       checklist is not. */
    .draft-banner {
      border: 3px solid #b91c1c;
      background: #fef2f2;
      color: #7f1d1d;
      padding: 12px 16px;
      margin: 0 0 20px;
      text-align: center;
      font-weight: bold;
      font-size: 13pt;
      letter-spacing: 0.05em;
    }

    .draft-banner .draft-sub {
      display: block;
      font-weight: normal;
      font-size: 10pt;
      letter-spacing: normal;
      margin-top: 4px;
    }

    .draft-watermark {
      position: fixed;
      top: 45%;
      left: 0;
      right: 0;
      text-align: center;
      font-size: 72pt;
      font-weight: bold;
      color: rgba(185, 28, 28, 0.12);
      transform: rotate(-30deg);
      pointer-events: none;
      z-index: 0;
      letter-spacing: 0.1em;
    }

    body {
      font-family: 'Times New Roman', Times, serif;
      line-height: 1.6;
      color: #000;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 20px;
      background: white;
    }

    h1 {
      font-size: 18pt;
      font-weight: bold;
      text-align: center;
      margin: 20px 0 6px 0;
      text-transform: uppercase;
    }

    h2 {
      font-size: 14pt;
      font-weight: bold;
      margin: 20px 0 10px 0;
      border-bottom: 2px solid #000;
      padding-bottom: 5px;
    }

    h3 {
      font-size: 12pt;
      font-weight: bold;
      margin: 15px 0 8px 0;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 10px 0;
    }

    th, td {
      border: 1px solid #000;
      padding: 8px;
      text-align: left;
    }

    th {
      background-color: #f0f0f0;
      font-weight: bold;
    }

    /* Property, customer, permit, jurisdiction and date on one line under the
       title, so the AHJ can confirm what they are holding before reading the
       determination. Every field here also appears in Project and Preparer. */
    .identity {
      text-align: center;
      font-size: 11pt;
      margin: 0 0 18px;
      padding-bottom: 12px;
      border-bottom: 1px solid #000;
    }

    .identity-property {
      display: block;
      font-weight: bold;
      font-size: 12pt;
    }

    .identity-meta {
      color: #374151;
    }

    /* Provenance for the summary table — interval, period and peak, so the
       numbers above are self-justifying without turning the page. */
    .basis-note {
      font-size: 10pt;
      color: #374151;
      margin: 4px 0 0;
    }

    /* Supporting detail runs two columns wide to keep the reference material
       short. Each column is a label/value pair list. */
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px 32px;
      margin: 10px 0;
    }

    .two-col h3 {
      margin-top: 0;
    }

    .pairs {
      margin: 0;
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 2px 10px;
    }

    .pairs dt {
      font-weight: bold;
    }

    .pairs dd {
      margin: 0;
    }

    .info-grid {
      display: grid;
      grid-template-columns: 200px 1fr;
      gap: 8px;
      margin: 10px 0;
    }

    .info-label {
      font-weight: bold;
    }

    .result-box {
      border: 3px solid ${isPassing ? '#22c55e' : '#ef4444'};
      background-color: ${isPassing ? '#f0fdf4' : '#fef2f2'};
      padding: 20px;
      margin: 20px 0;
      text-align: center;
    }

    .result-box h2 {
      border: none;
      color: ${isPassing ? '#166534' : '#991b1b'};
      margin: 0 0 10px 0;
    }

    .result-box p {
      margin: 0;
      font-size: 11pt;
    }

    .disclaimer {
      background-color: #f9fafb;
      border: 1px solid #d1d5db;
      padding: 15px;
      margin: 20px 0;
      font-size: 10pt;
    }

    .summary-table td:first-child {
      font-weight: bold;
      width: 60%;
    }

    .summary-table td:last-child {
      text-align: right;
    }

    .highlight-row {
      background-color: #eff6ff;
      font-weight: bold;
    }

    .total-row {
      background-color: #f3f4f6;
      font-weight: bold;
    }
  </style>
</head>
<body>
  ${data.firm_logo_src ? `
  <div class="letterhead"><img src="${attr(data.firm_logo_src)}" alt="${attr(data.company_name) || 'Firm logo'}"></div>` : ''}

  ${data.is_draft ? `
  <div class="draft-watermark">DRAFT</div>
  <div class="draft-banner">
    DRAFT — INCOMPLETE
    <span class="draft-sub">
      Required information is missing from this evaluation. This document is not
      suitable for submission to an authority having jurisdiction.
    </span>
  </div>` : ''}

  <h1>${reportTitle}</h1>

  <div class="identity">
    ${propertyLine ? `<span class="identity-property">${safe(propertyLine)}</span>` : ''}
    <span class="identity-meta">${identityMeta}</span>
  </div>

  <div class="result-box">
    <h2>SERVICE ADEQUACY DETERMINATION: ${safe(data.adequacy_result)}</h2>
    <p>${safe(data.adequacy_status_text)}</p>
  </div>

  ${isTraditional ? `
  <h2>NEC ${safe(data.trad_citation)} Load Calculation Summary</h2>
  <table class="summary-table">
    <tbody>
      ${tradSummaryRows}
    </tbody>
  </table>
  ` : `
  <h2>NEC ${cite} Load Calculation Summary</h2>
  <table class="summary-table">
    <tbody>
      <tr>
        <td>Existing Maximum Demand (from AMI data)</td>
        <td>${safe(data.existing_demand_kva)} kVA</td>
      </tr>
      <tr>
        <td>125% of Existing Demand (per NEC ${cite})</td>
        <td>${safe(data.existing_demand_125_kva)} kVA</td>
      </tr>
      <tr>
        <td>New Concurrent Load (Added Equipment${factored ? `, Table 120.83 demand factors applied to ${safe(data.total_concurrent_connected_kva)} kVA connected` : ''})</td>
        <td>${safe(data.total_concurrent_new_load_kva)} kVA</td>
      </tr>
${data.removed_credit_applied ? `
      <tr>
        <td>Removed Load Credit (40% of removed equipment running in the peak season)</td>
        <td>&minus;${safe(data.removed_credit_kva)} kVA</td>
      </tr>` : ''}
      <tr class="highlight-row">
        <td>Adjusted Demand (125% Existing + New Concurrent${data.removed_credit_applied ? ' &minus; Removed' : ''})</td>
        <td>${safe(data.adjusted_demand_kva)} kVA</td>
      </tr>
      <tr class="total-row">
        <td>Service Capacity (${safe(data.service_rating_amps)}A @ ${safe(data.service_voltage_text)})</td>
        <td>${safe(data.service_capacity_kva)} kVA</td>
      </tr>
      <tr style="background-color: ${isPassing ? '#dcfce7' : '#fee2e2'}; font-weight: bold;">
        <td>Available Margin</td>
        <td>${safe(data.available_margin_kva)} kVA</td>
      </tr>
      <tr>
        <td>Service Utilization</td>
        <td>${safe(data.utilization_percent)}%</td>
      </tr>
    </tbody>
  </table>
  `}
  <p class="basis-note">${safe(data.calculation_basis)}</p>

  ${(data.methods_summary_rows || []).length > 1 ? `
  <h2>Methods Computed</h2>
  <table>
    <thead>
      <tr>
        <th>Method</th>
        <th style="text-align:right">Calculated Demand</th>
        <th style="text-align:right">Available Margin</th>
        <th>Result</th>
        <th>Basis of Record</th>
      </tr>
    </thead>
    <tbody>
      ${data.methods_summary_rows.map((row: any) => `
      <tr${row.is_basis ? ' class="highlight-row"' : ''}>
        <td>${safe(row.label)}</td>
        <td style="text-align:right">${safe(row.demand_kva)} kVA</td>
        <td style="text-align:right">${safe(row.margin_kva)} kVA</td>
        <td>${safe(row.result)}</td>
        <td>${row.is_basis ? 'Basis of record' : ''}</td>
      </tr>`).join('')}
    </tbody>
  </table>
  ${data.basis_reason ? `<p class="basis-note">${safe(data.basis_reason)}</p>` : ''}
  ${data.inventory_check_line ? `<p class="basis-note">${safe(data.inventory_check_line)}</p>` : ''}
  <p class="basis-note">${safe(data.multi_method_line)}</p>
  ` : ''}

  <div class="page-break"></div>

  <h2>Project and Preparer</h2>
  <div class="two-col">
    <div>
      <h3>Project</h3>
      <dl class="pairs">
        <dt>Customer:</dt><dd>${safe(data.customer_name)}</dd>
        <dt>Property:</dt><dd>${safe(data.customer_address)}${data.customer_city_state_zip ? `<br>${safe(data.customer_city_state_zip)}` : ''}</dd>
        <dt>Permit No.:</dt><dd>${safe(data.permit_number) || 'N/A'}</dd>
        <dt>Jurisdiction:</dt><dd>${safe(data.ahj_jurisdiction) || 'N/A'}</dd>
        <dt>Report Date:</dt><dd>${safe(data.report_date)}</dd>
      </dl>
    </div>
    <div>
      <h3>Prepared By</h3>
      <dl class="pairs">
        <dt>Preparer:</dt><dd>${safe(data.prepared_by)}${data.preparer_title ? `, ${safe(data.preparer_title)}` : ''}</dd>
        <dt>License No.:</dt><dd>${safe(data.preparer_license) || 'N/A'}</dd>
        <dt>Company:</dt><dd>${safe(data.company_name) || 'N/A'}${data.company_address ? `<br>${safe(data.company_address)}` : ''}</dd>
      </dl>
    </div>
  </div>

  ${isTraditional ? `
  <h2>Existing Service and Dwelling Profile</h2>
  <div class="two-col">
    <div>
      <h3>Existing Service</h3>
      <dl class="pairs">
        <dt>Voltage:</dt><dd>${safe(data.service_voltage_text)}</dd>
        <dt>Rating:</dt><dd>${safe(data.service_rating_amps)} Amperes</dd>
        <dt>Capacity:</dt><dd>${safe(data.service_capacity_kva)} kVA</dd>
      </dl>
    </div>
    <div>
      <h3>Dwelling Profile</h3>
      <dl class="pairs">
        <dt>Floor Area:</dt><dd>${safe(data.trad_floor_area) || 'N/A'} ft²</dd>
        <dt>Small-Appliance Circuits:</dt><dd>${safe(data.trad_small_appliance_circuits)}</dd>
        <dt>Laundry Circuits:</dt><dd>${safe(data.trad_laundry_circuits)}</dd>
        <dt>Code Edition:</dt><dd>NEC ${safe(data.trad_edition)}</dd>${data.trad_qualification ? `
        <dt>Scope:</dt><dd>${safe(data.trad_qualification)}</dd>` : ''}
        <dt>Inventory:</dt><dd>${data.trad_inventory_attested
          ? 'Preparer attests the fastened-in-place appliance inventory is complete.'
          : 'Inventory completeness NOT attested.'}</dd>
      </dl>
    </div>
  </div>
  ${data.trad_defaults_disclosure ? `<p class="basis-note">${safe(data.trad_defaults_disclosure)}</p>` : ''}

  <h2>Load Worksheet — NEC ${safe(data.trad_citation)}</h2>
  <table>
    <thead>
      <tr>
        <th>Load</th>
        <th>Status</th>
        <th style="text-align:right">Connected (VA)</th>
        <th>Treatment</th>
        <th style="text-align:right">Demand (VA)</th>
      </tr>
    </thead>
    <tbody>
      ${tradWorksheetRows}
    </tbody>
  </table>
  <p class="basis-note">Pooled lines are demand-factored in aggregate — their individual
  demand appears in the summary tiers above, not per line.</p>
  ` : `
  <h2>Existing Service and Measured Demand</h2>
  <div class="two-col">
    <div>
      <h3>Existing Service</h3>
      <dl class="pairs">
        <dt>Voltage:</dt><dd>${safe(data.service_voltage_text)}</dd>
        <dt>Rating:</dt><dd>${safe(data.service_rating_amps)} Amperes</dd>
        <dt>Capacity:</dt><dd>${safe(data.service_capacity_kva)} kVA</dd>
      </dl>
    </div>
    <div>
      <h3>AMI Measurement</h3>
      <dl class="pairs">
        <dt>Provider:</dt><dd>${safe(data.ami_data_provider) || 'N/A'}</dd>
        <dt>Period:</dt><dd>${safe(data.ami_measurement_period) || 'N/A'}</dd>
        <dt>Interval:</dt><dd>${data.ami_interval_minutes ? safe(data.ami_interval_minutes) + ' minutes' : 'N/A'}</dd>
        <dt>Occupancy:</dt><dd>${safe(data.ami_occupancy_status) || 'N/A'}</dd>
        <dt>Peak Demand:</dt><dd>${safe(data.max_recorded_demand_kw)} kW (${safe(data.max_recorded_demand_kva)} kVA)</dd>
        <dt>Peak Time:</dt><dd>${safe(data.peak_timestamp)}${data.peak_season ? ` (${safe(data.peak_season)})` : ''}</dd>
      </dl>
    </div>
  </div>
  `}

  ${data.demand_chart_svg ? `
  <div class="figure">
    <h3>Daily Peak Demand</h3>
    ${data.demand_chart_svg}
    <p class="figure-caption">${safe(data.demand_chart_caption)}</p>
  </div>
  ` : ''}

  ${data.ami_site_notes ? `
  <h3>Notes on the Building</h3>
  <p style="white-space: pre-wrap">${safe(data.ami_site_notes)}</p>
  ` : ''}

  ${data.ami_data_quality_notes ? `
  <h3>Data Quality Notes</h3>
  <p>${safe(data.ami_data_quality_notes)}</p>
  ` : ''}

  ${data.ami_merge_disclosure ? `
  <h3>Interval Data Conflict Resolution</h3>
  <p>${safe(data.ami_merge_disclosure)}</p>
  ` : ''}

  ${data.data_quality_exclusions ? `
  <h3>Data Quality Exclusions</h3>
  <dl>
    <dt>Raw peak (all readings):</dt><dd>${safe(data.data_quality_exclusions.raw_peak)}</dd>
    <dt>Peak used in this calculation:</dt><dd>${safe(data.data_quality_exclusions.used_peak)}</dd>
    <dt>Total time excluded:</dt><dd>${safe(data.data_quality_exclusions.total_hours)}</dd>
  </dl>
  <table>
    <thead><tr><th>Excluded window</th><th>Flagged by</th><th>Reason</th><th>Note</th><th>Excluded by</th></tr></thead>
    <tbody>
      ${data.data_quality_exclusions.windows.map((w: { window: string; check: string; reason: string; note: string; by: string; at: string }) => `
      <tr><td>${safe(w.window)}</td><td>${safe(w.check)}</td><td>${safe(w.reason)}</td><td>${safe(w.note)}</td><td>${safe(w.by)}, ${safe(w.at)}</td></tr>`).join('')}
    </tbody>
  </table>
  <p>${safe(data.data_quality_exclusions.statement)}</p>
  ` : ''}

  <h2>Equipment Schedule - Proposed New Loads</h2>
  <table>
    <thead>
      <tr>
        <th>Equipment Name</th>
        <th>Type</th>
        <th>Source</th>
        <th>Qty</th>
        <th>Voltage</th>
        <th>Amps</th>
        <th>kW</th>
        <th>kVA</th>${isTraditional ? '' : `
        <th>Concurrent</th>`}${factored ? `
        <th>Factor</th>
        <th>Counted kVA</th>` : ''}
      </tr>
    </thead>
    <tbody>
      ${equipmentRows}
      <tr class="total-row">
        <td colspan="7">Total All New Equipment</td>
        <td>${safe(data.total_all_new_load_kva)}</td>${isTraditional ? '' : `
        <td></td>`}${factored ? `
        <td></td>
        <td></td>` : ''}
      </tr>${isTraditional ? '' : `
      <tr class="highlight-row">
        <td colspan="7">Total Concurrent New Equipment</td>
        <td>${safe(factored ? data.total_concurrent_connected_kva : data.total_concurrent_new_load_kva)}</td>
        <td></td>${factored ? `
        <td></td>
        <td>${safe(data.total_concurrent_new_load_kva)}</td>` : ''}
      </tr>`}
    </tbody>
  </table>

  ${removedSection}

  ${data.assumptions_notes ? `
  <h2>Assumptions and Notes</h2>
  <p style="white-space: pre-wrap;">${safe(data.assumptions_notes)}</p>
  ` : ''}

  <h2>Methodology</h2>
  ${isTraditional ? `
  <p>
    This evaluation was performed under ${safe(data.trad_submethod_text)}, the National
    Electrical Code's optional calculation for determining whether an existing dwelling
    service is of sufficient capacity to serve additional loads, as published in the
    ${safe(data.trad_edition)} edition. The existing load is established from the dwelling
    itself: general lighting and receptacles from floor area, small-appliance and laundry
    branch circuits at 1,500 VA each, and the nameplate ratings of appliances fastened in
    place, permanently connected, or on dedicated circuits. Existing and proposed loads are
    combined and the section's demand percentages applied as shown in the worksheet; no
    demand factor beyond those the section itself prescribes has been applied. This
    calculation describes the dwelling as it will be after the proposed work.
  </p>
  ${(data.trad_positions || []).length > 0 ? `
  <p><strong>Engine positions applied</strong> — where the code text is ambiguous, this
  evaluation takes the conservative reading and discloses it:</p>
  <ul>
    ${(data.trad_positions || []).map((p: string) => `<li style="font-size:10pt">${safe(p)}</li>`).join('')}
  </ul>` : ''}
  ${(data.trad_warnings || []).length > 0 ? `
  <p><strong>Calculation notes:</strong></p>
  <ul>
    ${(data.trad_warnings || []).map((w: string) => `<li style="font-size:10pt">${safe(w)}</li>`).join('')}
  </ul>` : ''}
  ` : `
  <!-- The measured method applies only to existing installations: a new service has no
       measured demand history to draw on. That scope statement is fixed prose
       here rather than a labelled field, because there is nothing for the user
       to choose. -->
  <p>
    The service evaluated here is an existing installation; NEC ${cite} establishes
    existing load by actual measurement and applies only where such a demand history exists.
    This evaluation was performed in accordance with the National Electrical Code (NEC)
    Section ${cite} (${safe(data.measured_article || 'Article 220')}, ${safe(data.code_edition || '2023')} edition).
    The existing demand was measured using Advanced Metering Infrastructure
    (AMI) data over the specified measurement period. The adjusted demand calculation applies
    a 125% factor to the maximum recorded existing demand and adds ${factored ? 'concurrent new loads at the Table 120.83 demand factors described below' : 'the full nameplate rating of concurrent new loads'}.
  </p>
  <p>
    NEC ${cite} is a self-contained alternative method. The 125% factor applied to measured existing demand is the method's built-in conservatism, accounting for load growth and measurement variability. ${factored ? 'Proposed new loads are entered at full nameplate (or MCA) rating and then take the Table 120.83 demand factors described below; no other multiplier is applied.' : 'Proposed new loads are entered at full nameplate (or MCA) rating with no further multiplier.'}
  </p>
  ${data.new_load_factor_disclosure ? `
  <p>
    <strong>New load under NEC ${cite} (${safe(data.code_edition)}):</strong> ${safe(data.new_load_factor_disclosure)}
  </p>` : ''}
  <p>
    The section states that one factor and then adds the new load without stating a multiplier for it. Whether a continuous load factor is owed on the new load is left open, and qualified opinion is divided; this evaluation applies none. That is TrueLoad's reading of an open provision, not a determination the Code makes, and the authority having jurisdiction may require otherwise. This evaluation compares calculated demand against the rating of the service only. The 125% factor for continuous loads elsewhere in the Code governs conductor and overcurrent device sizing, which is not performed here.${conductorRefs} Identifying which loads are continuous, and sizing for them, remains the responsibility of the licensed preparer.
  </p>
  `}

  ${hasMCAEquipment ? `
  <p>
    <strong>MCA-Based Ratings:</strong> One or more equipment items in this evaluation use Minimum Circuit Ampacity (MCA) ratings in place of nameplate kW values. MCA is derived from kVA = MCA &times; voltage &divide; 1000. MCA is a code-minimum conductor sizing value and may overstate actual operating load; nameplate data should be used wherever available. Every rating is used exactly as entered: no conversion is performed between MCA and nameplate in either direction, so an MCA-rated row is carried at its stated MCA value.
  </p>
  ` : ''}

  ${data.demand_adjusted_for_solar && data.gross_peak_kw ? `
  <p><strong>Note:</strong> On-site solar generation was detected in the AMI data. Peak demand has been calculated on a gross basis (consumption plus export at each interval) in accordance with NEC ${cite} Condition (1). Gross peak demand used: ${safe(parseFloat(data.gross_peak_kw).toFixed(2))} kW (${safe((parseFloat(data.gross_peak_kw) / 0.240).toFixed(1))} A at 240V).</p>
  ` : ''}

  ${data.ami_generation_column_detected && !data.demand_adjusted_for_solar ? `
  <p><strong>Note:</strong> On-site solar generation was detected but AMI data reflects net demand only. Gross demand could not be recovered from the available data. The calculation proceeds on measured net demand per NEC ${cite} Condition (1). The licensed preparer and AHJ should evaluate whether net demand adequately represents whole-building load for this installation.</p>
  ` : ''}

  <p><strong>Methodology Version:</strong> ${safe(data.methodology_version)}</p>
  ${!isTraditional && data.edition_note ? `
  <p style="font-size:10pt">${safe(data.edition_note)}</p>
  ` : ''}

  <div class="disclaimer">
    <p><strong>IMPORTANT DISCLAIMER:</strong></p>
    <p>
      This evaluation addresses service capacity only under NEC ${isTraditional ? safe(data.trad_citation) : cite} and does not waive any
      other Code requirements including conductor sizing, overcurrent protection, branch circuits,
      grounding and bonding, or other applicable standards. All installations must comply with
      all applicable provisions of the National Electrical Code and local amendments. Final
      approval remains subject to the Authority Having Jurisdiction (AHJ).
    </p>
    <p style="margin-top: 10px;">
      This report is based on the information provided${isTraditional ? '' : ' and AMI data available'} at the time of
      analysis. Future changes in occupancy, usage patterns, or additional loads may affect
      service adequacy.
    </p>
  </div>

  <div style="margin-top: 40px; border-top: 2px solid #000; padding-top: 20px;">
    <div class="info-grid">
      <div class="info-label">Prepared By:</div>
      <div>${safe(data.prepared_by)}</div>

      <div class="info-label">License Number:</div>
      <div>${safe(data.preparer_license) || 'N/A'}</div>

      <div class="info-label">Date:</div>
      <div>${safe(data.report_date)}</div>

      <div class="info-label">Signature:</div>
      <div style="border-bottom: 1px solid #000; height: 40px;"></div>
    </div>
  </div>
</body>
</html>
  `;
}
